import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { GENERAL_TYPES } from "../../constants/constants";
import SidebarToggle from "../../shared/molecules/SidebarToggle/SidebarToggle";
import SidePanelTab from "../../shared/molecules/SidePanelTab/SidePanelTab";
import { getEntityTypeDisplay } from "../../utils/entityDisplayUtils";
import { newPath } from "../../utils/newPath";
import { StrategymapSidePanelBody } from "./components/StrategymapSidePanelBody/StrategymapSidePanelBody";
import './StrategymapSidePanel.css';
import * as def from './StrategymapSidePanel.def';
import { StrategyMapViewSidePanelState, StrategyMapViewSidePanelTab } from "./StrategymapSidePanel.def";
import store from "../../redux/store";
import { setActiveTab } from "../../redux/slices/timeline";


export const StrategyMapViewSidePanel = ({
  isSimulation = false,
  collapsed = false,
  onToggle,
  isLoading,
  events,
  strategyMapData,
}: def.StrategyMapViewSidePanelProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();


  // Computing `currentTab` from `store` and `searchParams`

  const computeCurrentTab = useMemo(()=>{
    const allPossibleTabs = ["projects", "dependencies", "milestones"];

    const tab = (store.getState().timeline.activeTab || searchParams.get("tab")) as StrategyMapViewSidePanelTab;

    if(allPossibleTabs.includes(tab)) return tab;

    return "projects";
  }, [searchParams]);



  const [strategyMapViewState, setStrategyMapViewState] =
    useState<StrategyMapViewSidePanelState>({
      activeTab: computeCurrentTab,
      search: "",
      isLoading,
      dependencySearch: "",
      isAddingDep: false,
      isAddingMilestone: false,
      statusFilters: [],
      statusDropdownOpen: false,
    });

  const projectStatusCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = {};
    const s = (strategyMapViewState.search || "").toLowerCase();
    strategyMapData?.entities.forEach((project: any) => {
      const matchesSearch =
        !s || (project.name || "").toLowerCase().includes(s);
      if (!matchesSearch) return;
      const currentStatus = isSimulation
        ? project.sim_data?.status || project.status
        : project.status;
      const status = (currentStatus || "").toLowerCase();
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [strategyMapData, strategyMapViewState.search, isSimulation]);

  const filteredProjects = useMemo(
    () =>
      strategyMapData?.entities.filter((project) => {
        const currentStatus = isSimulation
          ? project.sim_data?.status || project.status
          : project.status;
        const status = (currentStatus || "").toLowerCase();

        if (
          strategyMapViewState.statusFilters.length > 0 &&
          !strategyMapViewState.statusFilters.includes(status)
        )
          return false;
        if (strategyMapViewState.search) {
          const s = strategyMapViewState.search.toLowerCase();
          const p = project as any;
          const id = (p.project_id || p.id || "").toString().toLowerCase();
          const name = (project.name || "").toLowerCase();

          const entityInfo = getEntityTypeDisplay(project.entity_type, project.type);
          const entityType = GENERAL_TYPES?.[entityInfo.type as keyof typeof GENERAL_TYPES]?.toLowerCase() || "";

          // Use exact match for ID search as requested
          if (!name.includes(s) && id !== s && entityType !== s) return false;
        }
        return true;
      }),
    [
      strategyMapData,
      strategyMapViewState.search,
      strategyMapViewState.statusFilters,
      isSimulation,
    ],
  );

  const internalState = {
    get: strategyMapViewState,
    set: setStrategyMapViewState,
  };

  useEffect(() => {
    navigate(
      newPath({
        tab: strategyMapViewState.activeTab,
      }),
    );
    events?.onViewChange?.(strategyMapViewState.activeTab);
    store.dispatch(setActiveTab(strategyMapViewState.activeTab));

  }, [strategyMapViewState.activeTab]);

  useEffect(()=>{
    setStrategyMapViewState((prev) => ({
      ...prev,
      isLoading,
    }))
  }, [isLoading])
  return (
    <div className="flex flex-col h-full bg-light-surfacePrimary dark:bg-secondary-dark border-r border-light-border-default dark:border-gray-700">
      <div className="sidebar-header">
        <div className="sidebar-tab-container">
          <div className="sidebar-tab-list">
            {(() => {
              const tabs: StrategyMapViewSidePanelTab[] = [
                "projects",
                "dependencies",
                "milestones",
              ];
              const labels: Record<StrategyMapViewSidePanelTab, string> = {
                projects: "Work Items",
                milestones: "Milestones",
                dependencies: "Dependencies",
              };

              const getBasis = (tab: StrategyMapViewSidePanelTab) => {
                const isActive = tab === strategyMapViewState.activeTab;
                return isActive ? "60%" : "20%";
              };

              return tabs.map((tab) => (
                <SidePanelTab
                  key={tab}
                  label={labels[tab]}
                  isActive={strategyMapViewState.activeTab === tab}
                  basis={getBasis(tab)}
                  onClick={() =>{

                    setStrategyMapViewState((prev) => ({
                      ...prev,
                      activeTab: tab,
                    }))
                    
                    store.dispatch(setActiveTab(tab));
                  }
                  }
                />
              ));
            })()}
          </div>
        </div>
        <SidebarToggle
          isCollapsed={collapsed}
          onToggle={() => onToggle?.(!collapsed)}
        />
      </div>

      <StrategymapSidePanelBody
        isSimulation={isSimulation}
        collapsed={collapsed}
        filteredProjects={filteredProjects}
        projectStatusCounts={projectStatusCounts}
        events={events}
        state={internalState}
        strategyMapData={strategyMapData}
      />
    </div>
  );
};